import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: "7", label: "Ostatnie 7 dni" },
  { value: "30", label: "Ostatnie 30 dni" },
  { value: "90", label: "Ostatnie 90 dni" },
  { value: "365", label: "Ostatni rok" },
];

const TIER_LABEL = {
  starter: "Starter",
  business: "Business",
  premium: "Premium",
  none: "Brak",
};

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  return Number(n || 0).toLocaleString("pl-PL");
}

function fmtPct(num, denom) {
  if (!denom) return "0.0%";
  return `${((num / denom) * 100).toFixed(1)}%`;
}

function StatCard({ label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-sm text-slate-500">{label}</p>
        <p className="mt-1 text-3xl font-bold text-slate-900">{value}</p>
        {hint ? <p className="mt-1 text-xs text-slate-400">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

function ByDayChart({ rows, metric }) {
  // Pure-CSS bar chart. metric is the key to pull from each row.
  const max = useMemo(() => {
    if (!rows || rows.length === 0) return 0;
    return rows.reduce((m, r) => Math.max(m, Number(r[metric] || 0)), 0);
  }, [rows, metric]);

  if (!rows || rows.length === 0) {
    return <p className="text-sm text-slate-500">Brak danych w tym zakresie.</p>;
  }

  return (
    <div>
      <div className="flex items-end gap-[2px] h-40 border-b border-slate-200">
        {rows.map((r) => {
          const v = Number(r[metric] || 0);
          const h = max > 0 ? Math.max(2, Math.round((v / max) * 100)) : 0;
          return (
            <div
              key={r.date}
              className="flex-1 bg-amber-400 hover:bg-amber-500 transition-colors"
              style={{ height: `${h}%` }}
              title={`${r.date}: ${metric === "revenue" ? fmtMoney(v) + " PLN" : fmtInt(v)}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("newUsers");

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/admin/analytics?days=${days}`, { withCredentials: true })
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch(() => toast.error("Nie udalo sie pobrac analityki"))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [days]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary || {};
  const tierBreakdown = data.tierBreakdown || [];
  const topCategories = data.topCategories || [];
  const byDay = data.byDay || [];

  const conversionRate = fmtPct(s.convertedLeads, s.totalLeads);
  const publishedRate = fmtPct(s.publishedBusinesses, s.totalBusinesses);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analityka</h1>
          <p className="text-slate-500">Przeglad metryk platformy lokalnie.pro</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-full sm:w-56" data-testid="range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Uzytkownicy"
          value={fmtInt(s.totalUsers)}
          hint={`+${fmtInt(s.newUsers)} w ostatnich ${data.days} dniach`}
        />
        <StatCard
          label="Firmy"
          value={fmtInt(s.totalBusinesses)}
          hint={`${fmtInt(s.publishedBusinesses)} opublikowanych (${publishedRate})`}
        />
        <StatCard
          label="Leady"
          value={fmtInt(s.totalLeads)}
          hint={`${fmtInt(s.convertedLeads)} skonwertowanych (${conversionRate})`}
        />
        <StatCard
          label="Platnosci"
          value={fmtInt(s.totalPayments)}
          hint="Wszystkie statusy"
        />
        <StatCard
          label="Przychod calkowity"
          value={`${fmtMoney(s.revenueTotal)} ${s.currency || "PLN"}`}
          hint="Tylko platnosci zakonczone"
        />
        <StatCard
          label="MRR"
          value={`${fmtMoney(s.mrr)} ${s.currency || "PLN"}`}
          hint="Aktywne subskrypcje / 12"
        />
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg">Aktywnosc dzienna</CardTitle>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-full sm:w-56" data-testid="metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newUsers">Nowi uzytkownicy</SelectItem>
              <SelectItem value="newBusinesses">Nowe firmy</SelectItem>
              <SelectItem value="newPayments">Nowe platnosci</SelectItem>
              <SelectItem value="revenue">Przychod (PLN)</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <ByDayChart rows={byDay} metric={metric} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Aktywne subskrypcje (tier)</CardTitle></CardHeader>
          <CardContent>
            {tierBreakdown.length === 0 ? (
              <p className="text-sm text-slate-500">Brak aktywnych subskrypcji.</p>
            ) : (
              <div className="space-y-2">
                {tierBreakdown.map((row) => (
                  <div key={row.tier} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <span className="text-sm capitalize">{TIER_LABEL[row.tier] || row.tier}</span>
                    <Badge variant="secondary">{fmtInt(row.count)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Najpopularniejsze branze</CardTitle></CardHeader>
          <CardContent>
            {topCategories.length === 0 ? (
              <p className="text-sm text-slate-500">Brak danych.</p>
            ) : (
              <div className="space-y-2">
                {topCategories.map((row) => (
                  <div key={row.category} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <span className="text-sm">{row.category}</span>
                    <Badge variant="secondary">{fmtInt(row.count)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
